/**
 * UI state of the standalone web: the one overlay on screen, the action to resume after signing
 * up or paying (§5.1), and the toasts that carry an action ("Deshacer", §5.7).
 */
import type { SongDTO, WebSongDTO } from '@akpl/shared';
import { create } from 'zustand';

export type WebSong = SongDTO | WebSongDTO;

export type Overlay =
  | { kind: 'login' }
  | { kind: 'signup' }
  | { kind: 'reset'; email?: string }
  | { kind: 'resetSent'; email: string }
  /** E-06: the action needs an account. */
  | { kind: 'requireAccount' }
  | { kind: 'paywall'; song?: WebSong }
  /** W-04 on desktop, MW-02 / MW-02b on a phone. */
  | { kind: 'song'; song: WebSong }
  /** §5.9, no frame: the session was closed because the account is blocked or deleted. */
  | { kind: 'accountClosed'; reason: 'blocked' | 'deleted' };

/** What the user wanted before an account or a payment got in the way. */
export type Intent =
  | { type: 'sing'; songId: string; tone?: string }
  | { type: 'openSong'; songId: string }
  | { type: 'favorite'; songId: string }
  | { type: 'party' }
  | { type: 'path'; to: string };

const INTENT_KEY = 'akp.web.intent';

/** In `sessionStorage` because it has to survive the round trip to Stripe in the same tab. */
export function saveIntent(intent: Intent): void {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* private mode: the action is simply not resumed */
  }
}

export function takeIntent(): Intent | null {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    sessionStorage.removeItem(INTENT_KEY);
    return raw ? (JSON.parse(raw) as Intent) : null;
  } catch {
    return null;
  }
}

/** Reads without consuming: E-06 and W-08 adapt their copy to the action that is waiting. */
export function peekIntent(): Intent | null {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    return raw ? (JSON.parse(raw) as Intent) : null;
  } catch {
    return null;
  }
}

export interface WebToast {
  id: number;
  message: string;
  tone: 'info' | 'error';
  action?: { label: string; run: () => void };
  ttlMs: number;
}

interface WebUiState {
  overlay: Overlay | null;
  /** E-06 → W-08 → W-05: after signing up with a Premium song, the paywall opens next. */
  paywallAfterAuth: boolean;
  toasts: WebToast[];
  /** The player covers the page: what floats goes over the video, not over its panel (E-22, E-31). */
  stageUp: boolean;
  setStageUp(value: boolean): void;
  openOverlay(overlay: Overlay): void;
  closeOverlay(): void;
  setPaywallAfterAuth(value: boolean): void;
  showToast(toast: Omit<WebToast, 'id' | 'ttlMs' | 'tone'> & Partial<WebToast>): void;
  dismissToast(id: number): void;
}

let nextToastId = 1;

export const useWebUi = create<WebUiState>((set) => ({
  overlay: null,
  paywallAfterAuth: false,
  toasts: [],
  stageUp: false,
  setStageUp: (stageUp) => set({ stageUp }),
  openOverlay: (overlay) => set({ overlay }),
  closeOverlay: () => set({ overlay: null }),
  setPaywallAfterAuth: (paywallAfterAuth) => set({ paywallAfterAuth }),
  showToast: (toast) =>
    set((state) => ({
      toasts: [...state.toasts, { tone: 'info', ttlMs: 5000, ...toast, id: nextToastId++ }],
    })),
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));
